import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  // Leave room for at least one 110px product image and a little breathing
  // room. A technically scrollable 40px strip is not a usable cart body.
  #minimumItemsHeight = 120;

  /** @type {Set<Element>} */
  #resizeTargets = new Set();
  #resizeObserver = new ResizeObserver(() => this.#updateStickyState());
  #layoutObserver = new MutationObserver(() => {
    // Section rendering can replace the summary without replacing this
    // component. Only rewire the observer when those actual nodes change.
    if (this.isConnected && this.#syncResizeTargets()) this.#updateStickyState();
  });

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  #connectedDrawer = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#connectedDrawer = this.#themeDrawer;
    this.#connectedDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#layoutObserver.observe(this, { childList: true, subtree: true });
    this.#syncResizeTargets();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#connectedDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#connectedDrawer = null;
    this.#resizeObserver.disconnect();
    this.#layoutObserver.disconnect();
    this.#resizeTargets.clear();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // c27: no optimistic open. Opening at click time showed the previous cart
    // for ~1s and opened even when the add then failed; the drawer opens below,
    // once the add has succeeded.

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #syncResizeTargets() {
    const dialog = this.#dialog;
    const next = new Set(
      [dialog, dialog?.querySelector('.theme-drawer__header'), dialog?.querySelector('.cart-drawer__summary')].filter(
        (element) => element
      )
    );
    let changed = false;
    for (const element of this.#resizeTargets) {
      if (!next.has(element)) {
        this.#resizeObserver.unobserve(element);
        this.#resizeTargets.delete(element);
        changed = true;
      }
    }
    for (const element of next) {
      if (element && !this.#resizeTargets.has(element)) {
        this.#resizeObserver.observe(element);
        this.#resizeTargets.add(element);
        changed = true;
      }
    }
    return changed;
  }

  #updateStickyState() {
    if (!this.isConnected) return;
    this.#syncResizeTargets();
    const dialog = this.#dialog;
    // Closed dialogs and their first zero-sized ResizeObserver delivery do
    // not provide meaningful geometry. Opening recalculates from real sizes.
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (drawerHeight <= 0) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const headerHeight = dialog.querySelector('.theme-drawer__header')?.getBoundingClientRect().height ?? 0;
    const ratio = summaryHeight / drawerHeight;
    const itemsHeight = drawerHeight - headerHeight - summaryHeight;
    const sticky = ratio <= this.#summaryThreshold && itemsHeight >= this.#minimumItemsHeight ? 'true' : 'false';
    if (dialog.getAttribute('cart-summary-sticky') !== sticky) dialog.setAttribute('cart-summary-sticky', sticky);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
